// Precondition: Shadow DOM is available, host element exists
// Postcondition: Fully functional tabs component with keyboard navigation and ARIA support
class TabsComponent extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
  }

  private render(): void {
    const tabItems = this.getTabItems();

    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = `
      <div class="tabs" role="tablist" aria-label="${this.getAttribute("label") || "Tabs"}">
        ${tabItems.map((tab, index) => `
          <button
            class="tab"
            role="tab"
            id="t-${tab.id}"
            aria-selected="${index === 0 ? "true" : "false"}"
            aria-controls="p-${tab.id}">
            ${tab.label}
          </button>
        `).join("")}
      </div>

      <div class="panels">
        <slot></slot>
      </div>
    `;

    if (this.shadowRoot) {
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
      }

      .tabs {
        display: flex;
        gap: 8px;
        border-bottom: 1px solid var(--border, #e5e7eb);
        margin-bottom: var(--space-md);
      }

      .tab {
        appearance: none;
        background: transparent;
        border: none;
        border-bottom: 2px solid transparent;
        color: var(--muted, #6b7280);
        padding: 12px 20px;
        font: 600 14px/1.2 Inter, system-ui, sans-serif;
        cursor: pointer;
        transition: all 0.2s ease;
        position: relative;
        white-space: nowrap;
      }

      .tab:hover {
        background: var(--tab-bg, #f4f4f5);
        border-bottom-color: var(--border, #e5e7eb);
      }

      .tab[aria-selected="true"] {
        background: var(--tab-bg, #f4f4f5);
        border-bottom-color: var(--primary, #e0524d);
        color: var(--text, #1c1c1e);
      }

      .tab:focus {
        outline: 2px solid var(--text, #1c1c1e);
        outline-offset: 2px;
      }

      .tab:active {
        transform: translateY(1px);
      }

      .panels {
        position: relative;
      }

      ::slotted([role="tabpanel"]) {
        display: none;
      }

      ::slotted([role="tabpanel"][data-active="true"]) {
        display: block;
      }

      @media (prefers-color-scheme: dark) {
        .tabs {
          border-bottom-color: var(--border, #374151);
        }

        .tab {
          color: var(--muted, #9ca3af);
        }

        .tab:hover {
          background: var(--tab-bg, #1a1a1a);
          border-bottom-color: var(--border, #374151);
        }

        .tab[aria-selected="true"] {
          background: var(--tab-bg, #1a1a1a);
          border-bottom-color: var(--primary, #e0524d);
          color: var(--text, #f3f4f6);
        }

        .tab:focus {
          outline-color: var(--text, #f3f4f6);
        }
      }
    `;
  }

  private getTabItems(): Array<{ id: string; label: string }> {
    const panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    return panels.map((panel) => ({
      id: panel.id.replace("p-", ""),
      label: panel.getAttribute("aria-label") || panel.id,
    }));
  }

  private setupEventListeners(): void {
    if (!this.shadowRoot) return;

    const tabs = this.shadowRoot.querySelectorAll(".tab");
    tabs.forEach((tab) => {
      tab.addEventListener("click", (e: Event) => this.handleTabClick(e));
      tab.addEventListener("keydown", (e: Event) => this.handleKeyDown(e as KeyboardEvent));
    });
  }

  private handleTabClick(event: Event): void {
    const clickedTab = event.currentTarget as HTMLElement;
    const tabId = clickedTab.id.replace("t-", "");
    this.activateTab(tabId);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (!this.shadowRoot) return;

    const tabs = Array.from(this.shadowRoot.querySelectorAll(".tab"));
    const currentIndex = tabs.indexOf(event.currentTarget as HTMLElement);

    let nextIndex: number;

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        (tabs[nextIndex] as HTMLElement).focus();
        break;
      case "ArrowRight":
        event.preventDefault();
        nextIndex =
          currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        (tabs[nextIndex] as HTMLElement).focus();
        break;
      case "Home":
        event.preventDefault();
        (tabs[0] as HTMLElement).focus();
        break;
      case "End":
        event.preventDefault();
        (tabs[tabs.length - 1] as HTMLElement).focus();
        break;
    }
  }

  private activateTab(tabId: string): void {
    if (!this.shadowRoot) return;

    // Update tab buttons
    const tabs = this.shadowRoot.querySelectorAll(".tab");
    tabs.forEach((tab) => {
      const isActive = tab.id === `t-${tabId}`;
      tab.setAttribute("aria-selected", isActive.toString());
    });

    // Update panels
    const panels = this.querySelectorAll('[role="tabpanel"]');
    panels.forEach((panel) => {
      const isActive = panel.id === `p-${tabId}`;
      panel.setAttribute("data-active", isActive.toString());
    });

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("tab-change", {
        detail: { tabId },
        bubbles: true,
        composed: true,
      })
    );
  }
}

// Register custom element (no export statement)

customElements.define("ft-tabs", TabsComponent);
